import type { ErrorCode, Locale } from '@sentra/contracts';
import { toErrorBody } from '@sentra/contracts';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { FailureDetailOptions, FailureDetails } from '@/api/details';
import { failureDetails } from '@/api/details';
import { failureMessage } from '@/api/errors';
import { useLocale } from '@/i18n';

/**
 * Turns anything thrown by the API client into one sentence in the operator's language.
 *
 * The mapping itself lives in `api/errors.ts` as a pure function so it can be tested against a
 * fixed catalogue; this is the three lines that hand it the two things only a component knows: the
 * active language, and the handful of sentences the wire has no code for.
 */
export function useApiErrorMessage(): (error: unknown) => string {
  const { t } = useTranslation();
  const { locale } = useLocale();

  return useCallback(
    (error: unknown) =>
      failureMessage(error, locale, {
        network: t('errors.networkBody'),
        unexpected: t('errors.unexpectedBody'),
        responseInvalid: t('errors.responseInvalid'),
        sessionInterrupted: t('errors.sessionInterrupted'),
        retryAfter: (seconds) => t('errors.retryAfter', { retryAfterSeconds: seconds }),
      }),
    [locale, t],
  );
}

/**
 * One error CODE, as the sentence the server would have sent for it.
 *
 * The catalogue that answers this is the contracts one, which the browser reaches through
 * `toErrorBody` rather than through `t()` — `failureMessage` above already does exactly that for a
 * refusal that arrived over the wire. This is the same rendering for a refusal the browser makes
 * FIRST, so that a rule enforced on both sides cannot be described two different ways: a band is
 * required in a pre-printed installation, and the field that says so and the 400 that would have
 * said it read from one sentence.
 *
 * Only for codes whose message needs no value the browser lacks. One that does would render with its
 * placeholder standing, which is why `failureMessage` falls back to the server's own text and this
 * has nothing to fall back to.
 */
export function useErrorCodeMessage(): (code: ErrorCode) => string {
  const { locale } = useLocale();

  return useCallback((code: ErrorCode) => toErrorBody({ code }, locale).error.message, [locale]);
}

/**
 * The per-field lines a refusal carries, in the operator's own language.
 *
 * The same three lines `useApiErrorMessage` is: the mapping lives in `api/details.ts` as a pure
 * function so it can be tested against fixed catalogues, and this hands it the two things only a
 * component knows — the active language, and the handful of sentences the wire has no code for.
 */
export function useFailureDetails(): (
  error: unknown,
  options?: FailureDetailOptions,
) => FailureDetails {
  const { t } = useTranslation();
  const { locale } = useLocale();

  return useCallback(
    (error: unknown, options?: FailureDetailOptions) =>
      failureDetails(
        error,
        locale,
        {
          pathSeparator: t('errors.detail.pathSeparator'),
          atIndex: (subject, index) => t('errors.detail.atIndex', { subject, index }),
          inLanguage: (subject, language) => t('errors.detail.inLanguage', { subject, language }),
          languageName: (tag: Locale) => t(`language.${tag}`),
        },
        options,
      ),
    [locale, t],
  );
}
